import { useEffect, useState } from "react";
import { Stack, Typography, Paper, Divider, Box } from "@mui/material";
import { Frame } from "../../components/layouts/Frame";
import { CreditData, UserApiData } from "../../types/index";
import { useAPI } from "../../hooks/useAPI";
import { useAuth } from "../../contexts/AuthContext";

const codeBoxStyle = {
  backgroundColor: "#f5f5f5",
  borderRadius: "8px",
  padding: "16px",
  fontFamily: "monospace",
  fontSize: "14px",
  whiteSpace: "pre-wrap",
  overflowX: "auto",
};

export const API = () => {
  const { fetchData } = useAPI();
  const { isAuthenticated } = useAuth();
  const [userApi, setUserApi] = useState<UserApiData>({});

  useEffect(() => {
    if (isAuthenticated) {
      fetchData("/user-api").then(({ errors, data }) => {
        setUserApi(data);
      });
    }
  }, [isAuthenticated]);

  return (
    <Frame title="API Details">
      <Stack spacing={4}>
        <Stack spacing={1}>
          <Typography
            variant="h4"
            fontWeight="600"
            color="#040505"
            lineHeight="200%">
            Your API Details Are:
            <br />
          </Typography>
          <Typography>
            <span style={{ fontWeight: "600" }}>Public: </span>{" "}
            {userApi.public_key ?? ""}{" "}
          </Typography>
          <Typography>
            <span style={{ fontWeight: "600" }}>Secret: </span>{" "}
            {userApi.secret_key ?? ""}{" "}
          </Typography>
        </Stack>

        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" fontWeight="600" color="primary">
            Authentication
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography gutterBottom>
            Use the following HTTP headers in every request:
          </Typography>
          <Box sx={codeBoxStyle}>
            X-API-Key: "your_api_key"
            <br />
            X-API-Secret: "your_api_secret"
          </Box>
        </Paper>
        <Stack spacing={1}>
          <Typography
            variant="h4"
            fontWeight="600"
            color="#040505"
            lineHeight="200%">
            Example API Requests:
            <br />
          </Typography>
          <Typography>
            <span style={{ fontWeight: "600" }}>
              {"https://api.aiassetregister.com/extract"}
            </span>{" "}
          </Typography>
          <Typography>
            <span style={{ fontWeight: "600" }}>{"Request body: "}</span>{" "}
          </Typography>
          <Typography>
            <pre>
              {JSON.stringify(
                {
                  image: "JDGyennkx77392.jpg",
                  detail: 3,
                  query: ["Serial Number", "Manufacturer", "Model Number"],
                },
                null,
                2,
              )}
            </pre>
          </Typography>
          <Typography>
            <span style={{ fontWeight: "600" }}>{"Response: "}</span>{" "}
          </Typography>
          <Typography>
            <pre>
              {JSON.stringify(
                {
                  "Job Ref": "AC127FD119E11",
                  "Credits Used": 15,
                  "Credits Remaining": 15,
                  "Query Made": 5,
                  "Serial Number": "123456789",
                  Manufacturer: "Dell",
                  "Model Number": "Latitude 5550",
                },
                null,
                2,
              )}
            </pre>
          </Typography>
          <Typography color="red">
            <span style={{ fontWeight: "600" }}>
              {"Response(No Credits): "}
            </span>{" "}
          </Typography>
          <Typography color="red">
            <pre>
              {JSON.stringify(
                {
                  Error:
                    "Not enough credits to process query, please purchase more credits on your account",
                },
                null,
                2,
              )}
            </pre>
          </Typography>

          <Typography
            variant="h4"
            fontWeight="600"
            color="#040505"
            lineHeight="200%">
            Example API Correction:
            <br />
          </Typography>
          <Typography>
            <span style={{ fontWeight: "600" }}>
              {"https://api.aiassetregister.com/correction"}
            </span>{" "}
          </Typography>
          <Typography>
            <span style={{ fontWeight: "600" }}>{"Request body: "}</span>{" "}
          </Typography>
          <Typography>
            <pre>
              {JSON.stringify(
                {
                  "Job Ref": "AC127FD119E11",
                  Information: {
                    "Serial Number": "123456789",
                    Manufacturer: "Dell",
                    "Model Number": "Latitude 5550",
                  },
                },
                null,
                2,
              )}
            </pre>
          </Typography>
          <Typography>
            <span style={{ fontWeight: "600" }}>{"Response: "}</span>{" "}
          </Typography>
          <Typography>
            <pre>
              {JSON.stringify(
                {
                  id: "Correction ID",
                  job_id: "Job ID",
                  Information: "JSON of Information",
                },
                null,
                2,
              )}
            </pre>
          </Typography>
        </Stack>
      </Stack>
    </Frame>
  );
};
